/** Models with a large enough context window to receive the full manuscript for Story Craft. */
export const STORYCRAFT_FULL_MANUSCRIPT_MODELS = new Set([
  "claude-opus-4-8",
  "claude-opus-4-7",
  "claude-opus-4-20250514",
  "claude-sonnet-5",
  "gpt-5.5",
  "gpt-5.4",
  "gpt-5.3-codex",
  "gpt-5.2",
  "o1-preview",
]);

export function storyCraftUsesFullManuscript(model: string | undefined): boolean {
  if (!model?.trim()) return true;
  return STORYCRAFT_FULL_MANUSCRIPT_MODELS.has(model.trim());
}

export type StoryCraftManuscriptChapter = {
  chapterId: string;
  title: string;
  order: number;
  text: string;
};

export function buildStoryCraftManuscriptUserPrompt(
  assessedChapterId: string,
  assessedChapterTitle: string,
  chapters: StoryCraftManuscriptChapter[]
): string {
  const sections = chapters.map((ch) => {
    const isTarget = ch.chapterId === assessedChapterId;
    const header = isTarget
      ? `## **CHAPTER UNDER ASSESSMENT** — Ch. ${ch.order}: ${ch.title} (chapter_id=${ch.chapterId})`
      : `## Ch. ${ch.order}: ${ch.title} (chapter_id=${ch.chapterId}) [context only]`;
    return `${header}\n\n${ch.text.trim() || "[empty]"}`;
  });

  return [
    "Analyze Story Craft for one chapter in the context of the full manuscript below.",
    "",
    `Assessed chapter: ${assessedChapterTitle} (chapter_id=${assessedChapterId})`,
    "",
    "---",
    "",
    sections.join("\n\n---\n\n"),
  ].join("\n");
}

export const STORYCRAFT_FULL_MANUSCRIPT_SYSTEM_INSTRUCTIONS = `**FULL MANUSCRIPT IN USER MESSAGE:** The user message includes every chapter of the book in reading order. Only the section marked **CHAPTER UNDER ASSESSMENT** is the chapter you score (1–5 dimensions), summarize (2–3 sentences), list **promisesMade** for, list **seeds** for, and suggest checklist improvements for. **promisesKept** still refers to the PREVIOUS NARRATIVE PROMISES list (if any) and how this assessed chapter addresses them. Use all other chapters strictly as context—continuity, callbacks, arc, pacing relative to neighbors, and whether this chapter fits the whole—not for separate scores or summaries. Never substitute plot from a context-only section for the assessed chapter's summary or scores.

**Seeds:** Identify narrative seeds planted in the assessed chapter that pay off in LATER chapters. A seed is a detail, line, object, setup, or moment that gains meaning when revisited later. Only include seeds where you can identify a concrete payoff in a later chapter (use chapter_id from section headers). If none apply, return an empty array.`;

export const STORYCRAFT_SEEDS_JSON_SCHEMA = `"seeds": [
    { "description": "what is planted in this chapter", "context": "brief quote or reference from this chapter", "payoffChapterId": "chapter id from header", "payoffChapterTitle": "later chapter title", "payoffDescription": "how the seed pays off later" }
  ]`;

export const VARIATION_FULL_MANUSCRIPT_SYSTEM_INSTRUCTIONS = `**FULL MANUSCRIPT IN USER MESSAGE:** The user message includes every chapter of the book in reading order. Only the section marked **CHAPTER TO REWRITE** is the chapter you rewrite. All other chapters are continuity context only—use them to keep names, places, timeline, and established facts consistent.

**Do not invent story content:** Never add characters, places, events, objects, backstory, or plot beats that are not established in the manuscript. When applying improvement checklist items or other editorial instructions, revise only within facts already present in the chapter to rewrite or established elsewhere in the book. If a checklist item would require new story content, address its spirit using existing material or leave that aspect unchanged.

Do NOT copy scenes or plot from other chapters into the rewrite unless they are already part of the chapter to rewrite.`;

export function buildVariationManuscriptUserPrompt(
  rewriteChapterId: string,
  rewriteChapterTitle: string,
  chapters: StoryCraftManuscriptChapter[]
): string {
  const sections = chapters.map((ch) => {
    const isTarget = ch.chapterId === rewriteChapterId;
    const header = isTarget
      ? `## **CHAPTER TO REWRITE** — Ch. ${ch.order}: ${ch.title} (chapter_id=${ch.chapterId})`
      : `## Ch. ${ch.order}: ${ch.title} (chapter_id=${ch.chapterId}) [context only]`;
    return `${header}\n\n${ch.text.trim() || "[empty]"}`;
  });

  return [
    "Rewrite one chapter using the full manuscript below for continuity.",
    "",
    `Chapter to rewrite: ${rewriteChapterTitle} (chapter_id=${rewriteChapterId})`,
    "",
    "---",
    "",
    sections.join("\n\n---\n\n"),
  ].join("\n");
}
